import { Box, Grid } from "@mui/material";
import ShareLink from "./components/ShareLink";
import ClientInfoCard from "./components/ClientInfoCard";
import DocumentsSection from "./components/DocumentsSection";
import StreamSection from "./components/StreamSection";
import Sidebar from "./components/Sidebar";
import ServiceCard from "./components/ServiceCard";
import { smartActions } from "../../data";
import DeceasedInfoCard from "./components/DeceasedInfoCard";
import { ActionItem } from "../../types/types";
import { Send } from "@mui/icons-material";
import { CalendarIcon, MessageIcon } from "../../../../../components/icons";
import { useBookingData } from "../../../view-booking/hooks/UseBookingData";
import { DocumentItem } from "../../types/documents";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookingData } from "../../../types/view-booking";

const actionItems: ActionItem[] = [
  {
    icon: <CalendarIcon sx={{ color: "blue" }} />,
    title: "30 days from Lisa Zeremende's aniniverssary",
    subtitle: "Send message to Lisa's family",
  },
  {
    icon: <MessageIcon sx={{ color: "#E11D48" }} />,
    title: "Request Client review",
    subtitle: "Gather insights from client for future improvements",
  },
  {
    icon: <Send />,
    title: "Send Thank you message to client",
    subtitle: "Reflect on project outcomes and team",
  },
];

type BookingStream = BookingData["streams"][number];
type UIStream = {
  id: string;
  title: string;
  date: string;
  time: string;
  locationName: string;
  locationAddress: string;
};

const BookingDetails = () => {
  const { bookingData, documents } = useBookingData();
  const { id } = useParams<{ id: string }>();
  const [docs, setDocs] = useState<DocumentItem[]>([]);

  useEffect(() => {
    setDocs(documents);
  }, [documents]);

  const navigate = useNavigate();
  const [streamTab, setStreamTab] = useState(0);

  const streams = bookingData?.streams ?? [];
  const streamTabs = streams.map((_, idx) => `Stream ${idx + 1}`);
  const activeStream = streams[streamTab];
  const currentStreamId = activeStream?.id;

  const handleViewStream = () => {
    if (!currentStreamId) return;
    navigate(`/stream-management/view/${currentStreamId}`);
  };

  const handleOpenCallsheet = () => {
    if (!currentStreamId) return;
    navigate(`/stream-management/view/${currentStreamId}/callsheet/`);
  };

  const handleOpenMemorialPage = () => {
    if (!id) return;

    const streamTitle = activeStream?.title || "";
    const prefix = "Stream for ";
    const nameForSlug = streamTitle.startsWith(prefix)
      ? streamTitle.slice(prefix.length)
      : streamTitle;

    const slug = encodeURIComponent(nameForSlug.trim().replace(/\s+/g, "-"));

    if (bookingData?.service === "Conference") {
      const conferenceSlug = encodeURIComponent(
        bookingData.conferenceDetails?.conference_title?.replace(/\s+/g, "-") ||
          "conference",
      );
      navigate(`/conference-page/${id}/${conferenceSlug}`);
      return;
    }

    if (bookingData?.service === "Church") {
      const churchTitle = encodeURIComponent(
        bookingData.churchDetails?.church_name?.replace(/\s+/g, "-") ||
          "church",
      );
      navigate(`/church-page/${id}/${churchTitle}`);
      return;
    }

    navigate(`/memorial-page/${id}/${slug}`);
  };

  const toUIStream = (s: BookingStream): UIStream => ({
    id: String(s.id ?? ""),
    title: s.title,
    date: s.date,
    time: `${s.start} - ${s.end}`,
    locationName: s.venueDetails?.name ?? "N/A",
    locationAddress: [s.addressOne, s.addressTwo, s.suburb, s.city]
      .filter((v): v is string => Boolean(v && v.trim()))
      .join(", "),
  });

  const uiStreams = streams.map(toUIStream);
  const uiActiveStream = uiStreams[streamTab];

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, md: 9 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <ShareLink />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%", height: "100%" }}>
            <Sidebar
              rating={70}
              smartActions={[]}
              actionItems={[]}
              mode="ratingOnly"
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <ClientInfoCard client={bookingData?.organiser} />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <DeceasedInfoCard deceased={bookingData?.beloved} />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%", height: "100%" }}>
            <Sidebar
              rating={70}
              smartActions={smartActions}
              actionItems={[]}
              mode="smartActionsOnly"
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <DocumentsSection
              documents={docs}
              bookingId={id ?? ""}
              onAddDocument={(newDoc) => setDocs((prev) => [newDoc, ...prev])}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <ServiceCard
              name={bookingData?.package?.name || ""}
              price={bookingData?.package?.price || 0}
              items={bookingData?.package?.items || []}
              total={bookingData?.package?.total || ""}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex" }}>
          <Box sx={{ width: "100%" }}>
            <Sidebar
              rating={70}
              smartActions={smartActions}
              actionItems={actionItems}
              mode="actionsOnly"
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 9 }}>
          {uiActiveStream && (
            <StreamSection
              streamTabs={streamTabs}
              stream={uiActiveStream}
              activeTab={streamTab}
              onTabChange={setStreamTab}
              onViewStream={handleViewStream}
              onOpenMemorialPage={handleOpenMemorialPage}
              onOpenCallsheet={handleOpenCallsheet}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default BookingDetails;
